import React, { Suspense, useEffect, useState } from 'react';
import { HashRouter, BrowserRouter } from 'react-router-dom';
import { PageLoader } from '@sumtotal/react-foundation';
import AppLayout from './common/layout/components/AppLayout';
import Breadcrumbs from './common/layout/components/Breadcrumbs';
import Config from './config/Config';
import authService from './authentication/AuthorizeService';
import CorePillarFrame from './modules/core/components/CorePillarFrame';
import CoreMessageListener from './modules/core/components/CoreMessageListener';
import SitemapProvider from './common/layout/components/SitemapProvider';
import bootstrapApplication from './modules/core/services/bootstrapService';
import './config/i18n';
import RouteChangeListener from './RouteChangeListener';
import SecurityQuestions from './modules/core/components/SecurityQuestions';
import Tracker from './analytics/Tracker';
import AppRoutes from './AppRoutes';
import MyGuideTracker from './analytics/MyGuideTracker';
import TrackingProvider from './analytics/TrackingProvider';
import getLogger from './utils/logger';
import MobileAppMenu from './common/topnav/components/MobileAppMenuHeader';
import CheckIntegrationSetting from './utils/integrationHelper';
import { IntegrationElement } from './utils/IntegrationConstants';
import useIntegrationContext from './common/hooks/useIntegrationContext';

// Use different router type depending on configuration
const AppRouterComponent = Config.navigationType === 'history' ? BrowserRouter : HashRouter;

const AppRouter = () => {
  const [userAuthStatus, setUserAuthStatus] = useState({
    isAuthenticated: false,
    emulatedUser: ''
  });

  // isNotMobileIntegRationMode used to set the Application layout for Web and Mobile browsers.
  const [isNotMobileIntegRationMode, setIsMobileIntegRationMode] = useState(true);

  const { integrationContext } = useIntegrationContext();

  // Set the Integration context mode.
  useEffect(() => {
    setIsMobileIntegRationMode(
      CheckIntegrationSetting(IntegrationElement.ShowMobileAppHeader, integrationContext.mode)
    );
  }, [integrationContext.mode]);

  useEffect(() => {
    authService
      .getUser()
      .then((user) => {
        if (user) {
          // To check if its emulation mode.
          if (
            user.profile &&
            user.profile.emulatorusername &&
            user.profile.emulatorusername !== ''
          ) {
            setUserAuthStatus({ isAuthenticated: true, emulatedUser: user.profile.propername });
          } else {
            setUserAuthStatus({ isAuthenticated: true, emulatedUser: '' });
          }
        }
        // bootstrap after successful auth
        if (user) {
          bootstrapApplication();
        }
        return null;
      })
      .catch((errorResponse) => {
        setUserAuthStatus({ isAuthenticated: false, emulatedUser: '' });
        getLogger().error(errorResponse);
      });
  }, []);

  return (
    <>
      <AppRouterComponent basename={Config.appPrefix}>
        {isNotMobileIntegRationMode && webAppLayout(userAuthStatus)}
        {!isNotMobileIntegRationMode && mobileAppLayout(userAuthStatus)}
      </AppRouterComponent>
    </>
  );
};

// This function used to set the web application layout for rcore pages by defualt.
function webAppLayout(userAuthStatus) {
  return (
    <AppLayout showStickyHeader={userAuthStatus.emulatedUser !== ''}>
      {userAuthStatus.isAuthenticated && (
        <SitemapProvider emulatedUser={userAuthStatus.emulatedUser} />
      )}
      {/* This component identifies the change in location path */}
      <RouteChangeListener />
      <div className="content grid-print-class">
        {userAuthStatus.isAuthenticated && <CorePillarFrame />}
        <CoreMessageListener />
        {userAuthStatus.isAuthenticated && (
          <TrackingProvider isinSite>
            <Tracker />
          </TrackingProvider>
        )}
        <MyGuideTracker />
        {userAuthStatus.isAuthenticated && <SecurityQuestions />}
        <div
          id="rcore-page-content"
          style={{
            overflow: 'auto',
            position: 'relative',
            height: '100%'
          }}
        >
          {userAuthStatus.isAuthenticated && <Breadcrumbs />}
          <Suspense fallback={<PageLoader show />}>
            <AppRoutes />
          </Suspense>
        </div>
      </div>
    </AppLayout>
  );
}

// This function used to set the mobile app layout for mobile specific rcore pages.
function mobileAppLayout(userAuthStatus) {
  return (
    <AppLayout showStickyHeader={userAuthStatus.emulatedUser !== ''}>
      {userAuthStatus.isAuthenticated && <MobileAppMenu />}
      <Suspense fallback={<PageLoader show />}>
        <AppRoutes />
      </Suspense>
    </AppLayout>
  );
}

export default AppRouter;
